import React, { ReactElement, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FormattedMessage, useIntl } from "react-intl";
import { FormikHelpers } from "formik";
import { apiToFormikErrors } from "../../utils/formHelpers";
import { toast } from "react-toastify";
import { ModalProps } from "react-bootstrap";
import LoadingPacman from "../layout/LoadingPacman";

export type SimpleEntityUpsertStepModalProps = {
    showModal: boolean;
    entityName: string;
    submitByStepsBody?: React.FC<any>;
    handleClose: () => void;
    entity?: { id: number; name: string; comment: string } | null;
    updateGridOnSave: (object: any, isNew: boolean) => void;
    maxSteps: number;
    saveHandler: (_values: any, objectId?: number, confirmationResponse?: any) => any;
    confirmationHandler?: (_values: any, objectId?: number) => any;
    confirmationModalCondition?: (value: any) => boolean;
    confirmationModalBody?: React.FC<any>;
    modalProps?: ModalProps;
};

export const SimpleEntityUpsertStepModal: React.FC<
    SimpleEntityUpsertStepModalProps & { children: ReactElement }
> = (props) => {
    const {
        entity,
        showModal,
        submitByStepsBody,
        confirmationHandler,
        confirmationModalCondition,
        handleClose,
        confirmationModalBody = () => <>NoBody</>,
        updateGridOnSave,
        children,
        entityName,
        maxSteps,
        saveHandler,
        modalProps,
    } = props;
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [upsertData, setUpsertData] = useState<any>(null);
    const [wasConfirmed, setWasConfirmed] = useState(false);
    const [confirmationResponse, setConfirmationResponse] = useState<any>();
    const [currentStep, setCurrentStep] = useState(0);
    const [disableSaveByStepButtons, setDisableSaveByStepButtons] = useState(true);

    const goToStep = (step: number) => {
        setCurrentStep(step);
    };

    const handleCloseWithStep = () => {
        handleClose();
        goToStep(0);
    };

    const enableCloseButton = () => {
        setDisableSaveByStepButtons(false);
        updateGridOnSave({}, true);
    };

    const handleCloseSaveModal = () => {
        if (!disableSaveByStepButtons) {
            setUpsertData(null);
        }
    };

    const onSubmit = async (_values: any, formikHelpers: FormikHelpers<any>) => {
        for (const key in _values) {
            if (_values[key] === "") _values[key] = undefined;
        }
        if (submitByStepsBody) {
            setUpsertData(_values);
            handleCloseWithStep();
            return;
        }
        setLoading(true);

        try {
            const res = await saveHandler(_values, entity?.id);
            await updateGridOnSave(res, !entity?.id);
            setLoading(false);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleCloseWithStep();
        } catch (error: any) {
            if (!error.response?.data.detail) {
                toast.error(
                    error?.response?.data?.errors[0]?.message ||
                        formatMessage({ id: "common.notification.error" })
                );
            } else {
                toast.error(error.response?.data.detail);
            }
            apiToFormikErrors(error?.response, formikHelpers);
        }
        setWasConfirmed(false);
        setLoading(false);
    };

    const confirmationRequest = async (_values: any, formikHelpers: FormikHelpers<any>) => {
        if (confirmationModalCondition && confirmationModalCondition(_values)) {
            setLoading(true);
            if (confirmationHandler)
                try {
                    setConfirmationResponse(await confirmationHandler(_values, entity?.id));
                    setLoading(false);
                    setShowConfirmationModal(true);
                } catch (error: any) {
                    if (!error.response?.data.detail) {
                        toast.error(
                            error?.response?.data?.errors[0]?.message ||
                                formatMessage({ id: "common.notification.error" })
                        );
                    } else {
                        toast.error(error.response?.data.detail);
                    }
                    apiToFormikErrors(error?.response, formikHelpers);
                }
            setLoading(false);
        } else {
        }
    };
    const onConfirmation = () => {
        setWasConfirmed(true);
        setShowConfirmationModal(false);
    };
    return (
        <>
            <Modal
                centered
                show={showModal}
                onHide={handleCloseWithStep}
                dialogClassName="simpleEntityGrid-modal"
                size={modalProps?.size || "lg"}
            >
                <LoadingPacman loading={loading} />
                <Modal.Header closeButton>
                    <Modal.Title>
                        {entity ? (
                            <>
                                <FormattedMessage id="common.action.edit" /> {entity?.name}
                            </>
                        ) : (
                            <>
                                <FormattedMessage id="common.action.add" /> {entityName}
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {React.cloneElement(children, {
                        maxSteps,
                        handleCloseWithStep,
                        currentStep,
                        goToStep,
                        onSubmit,
                        confirmationHandler: {
                            wasConfirmed,
                            setWasConfirmed,
                            confirmationRequest,
                            confirmationModalCondition,
                            confirmationResponse,
                        },
                    })}
                </Modal.Body>
                <Modal.Footer>
                    {currentStep > 0 && !entity && (
                        <Button
                            variant="secondary"
                            disabled={loading}
                            onClick={() => goToStep(currentStep - 1)}
                        >
                            <FormattedMessage id="common.action.return" />
                        </Button>
                    )}
                    {entity && (
                        <Button
                            variant="secondary"
                            disabled={loading}
                            onClick={handleCloseWithStep}
                        >
                            <FormattedMessage id="common.action.close" />
                        </Button>
                    )}

                    <Button
                        variant="primary"
                        form={`stepperForm${currentStep}`}
                        disabled={loading}
                        type="submit"
                    >
                        {currentStep == maxSteps - 1 && !entity ? (
                            <FormattedMessage id="common.action.save" />
                        ) : (
                            <FormattedMessage id="common.action.continue" />
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}

            <Modal centered style={{ background: "rgba(0,0,0,0.5)" }} show={showConfirmationModal}>
                <LoadingPacman loading={loading} />
                <Modal.Header closeButton onClick={() => setShowConfirmationModal(false)}>
                    <Modal.Title>
                        <FormattedMessage id="common.action.confirmation" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {React.createElement(confirmationModalBody, {
                        data: confirmationResponse,
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
                        <FormattedMessage id="common.action.cancel" />
                    </Button>

                    <Button variant="primary" onClick={onConfirmation}>
                        <FormattedMessage id="common.action.ok" />
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*save by Steps Modal */}
            {submitByStepsBody && upsertData && (
                <Modal centered style={{ background: "rgba(0,0,0,0.5)" }} show={!!upsertData}>
                    <LoadingPacman loading={loading} />
                    <Modal.Header
                        closeButton={!disableSaveByStepButtons}
                        onClick={handleCloseSaveModal}
                    >
                        <Modal.Title>
                            <FormattedMessage id="common.action.saving" /> :{" "}
                            {upsertData.domain || upsertData.name}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {React.createElement(submitByStepsBody, {
                            upsertData,
                            enableCloseButton,
                        })}
                    </Modal.Body>
                    <Modal.Footer>
                        {!disableSaveByStepButtons && (
                            <Button variant="primary" onClick={handleCloseSaveModal}>
                                <FormattedMessage id="common.action.ok" />
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};
